import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

// Base do backend (igual ao axiosConfig): vazio em dev (Vite proxy same-origin),
// host completo em prod.
const BASE_URL = (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? "";

// O consumer emite 1 NOTIFY por UPDATE/INSERT — um lote vira rajada de eventos.
const INVALIDATE_DEBOUNCE_MS = 1000;
const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30000;

/** Abre um EventSource (SSE) no canal do workspace enquanto montado e invalida o
 *  cache do autosped a cada evento (com debounce). Fecha no unmount.
 *
 *  A reconexão automática do EventSource só cobre queda de rede: resposta HTTP
 *  não-2xx (ex.: 401 com o access cookie vencido — SSE não passa pelo
 *  interceptor do axios) fecha a conexão DE VEZ. Por isso o onerror reabre com
 *  backoff, disparando antes um refetch via axios — que renova o cookie pelo
 *  interceptor de refresh e já atualiza a tela com o que se perdeu. */
export function useAutospedStream(workspaceId: number | null): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (workspaceId == null) return;
    const url = `${BASE_URL}/api/v1/workspaces/${workspaceId}/autosped/stream`;
    let es: EventSource | null = null;
    let invalidateTimer: number | null = null;
    let reconnectTimer: number | null = null;
    let attempts = 0;
    let disposed = false;

    const invalidate = () => {
      if (invalidateTimer != null) return; // trailing call já agendada
      invalidateTimer = window.setTimeout(() => {
        invalidateTimer = null;
        void qc.invalidateQueries({ queryKey: ["autosped", workspaceId] });
      }, INVALIDATE_DEBOUNCE_MS);
    };

    const open = () => {
      if (disposed) return;
      es = new EventSource(url, { withCredentials: true });
      es.onopen = () => {
        attempts = 0;
      };
      es.onmessage = invalidate;
      es.onerror = () => {
        if (disposed || es?.readyState !== EventSource.CLOSED) return;
        es.close();
        attempts += 1;
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempts - 1), RECONNECT_MAX_MS);
        reconnectTimer = window.setTimeout(() => {
          void qc.invalidateQueries({ queryKey: ["autosped", workspaceId] }).finally(open);
        }, delay);
      };
    };
    open();

    return () => {
      disposed = true;
      if (invalidateTimer != null) window.clearTimeout(invalidateTimer);
      if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
      es?.close();
    };
  }, [workspaceId, qc]);
}
